import { useMemo } from "react";
import { motion } from "framer-motion";
import { Cog, ShoppingBag, History, Cpu, Archive, AlertTriangle, ArrowRight } from "lucide-react";
import { systemHealth, systemNodes, SYSTEM_EDGES, SYSTEM_EDGE_LABELS } from "@/lib/systemMap";

const NODE_W = 210;
const NODE_H = 100;
const CANVAS_W = 940;
const CANVAS_H = 560;

const ACCENTS = {
  slate: "bg-slate-500/20 text-slate-300",
  blue: "bg-blue-500/20 text-blue-300",
  amber: "bg-amber-500/20 text-amber-300",
  violet: "bg-violet-500/20 text-violet-300",
  indigo: "bg-indigo-500/20 text-indigo-300",
  emerald: "bg-emerald-500/20 text-emerald-300",
  teal: "bg-teal-500/20 text-teal-300",
  rose: "bg-rose-500/20 text-rose-300",
};
const DOT = { red: "bg-rose-500", amber: "bg-amber-400", green: "bg-emerald-400", slate: "bg-slate-400" };
// Hoisted motion props: stable references so framer-motion doesn't see "new" objects on every render.
const NODE_INITIAL = { opacity: 0, y: 14, scale: 0.95 };
const NODE_ANIMATE = { opacity: 1, y: 0, scale: 1 };
const NODE_HOVER = { scale: 1.03 };
const ALERT_TONE = { red: "bg-rose-500 text-white", amber: "bg-amber-400 text-slate-900", slate: "bg-slate-600 text-slate-200" };

function Node({ n, index, onNavigate }) {
  const transition = useMemo(() => ({ delay: 0.05 * index, duration: 0.4, ease: "easeOut" }), [index]);
  const style = useMemo(() => ({ left: n.x, top: n.y, width: NODE_W, height: NODE_H }), [n.x, n.y]);
  return (
    <motion.button
      type="button"
      initial={NODE_INITIAL}
      animate={NODE_ANIMATE}
      transition={transition}
      whileHover={NODE_HOVER}
      onClick={() => n.tab && onNavigate(n.tab)}
      style={style}
      className="absolute text-left rounded-xl border border-slate-700 bg-slate-800/70 backdrop-blur-sm shadow-lg hover:border-blue-400/60 hover:shadow-blue-900/30 transition-colors p-3 flex flex-col justify-between group"
      data-testid={`sysmap-node-${n.id}`}
    >
      <span className={`absolute top-2.5 right-2.5 h-2.5 w-2.5 rounded-full ${DOT[n.health || "green"]} ${n.health === "red" ? "animate-pulse ring-2 ring-rose-500/30" : ""}`} />
      <div className="flex items-center gap-2.5">
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${ACCENTS[n.accent]}`}>
          <n.icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-100 leading-tight truncate">{n.title}</p>
          <p className="text-[11px] text-slate-400 truncate">{n.kicker}</p>
        </div>
      </div>
      <div className="flex items-end justify-between gap-1">
        <span className="text-[11px] font-mono-data text-slate-400 truncate">{n.stat}</span>
        {n.alert ? (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${ALERT_TONE[n.alertTone] || ALERT_TONE.slate}`}>{n.alert}</span>
        ) : n.tab ? (
          <ArrowRight className="h-3.5 w-3.5 text-slate-500 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
        ) : null}
      </div>
    </motion.button>
  );
}

function EdgeLabel({ x, y, children, tone = "slate" }) {
  const toneCls = tone === "amber" ? "text-amber-300 border-amber-500/30 bg-amber-500/10" : "text-slate-300 border-slate-600 bg-slate-800/80";
  return (
    <div className={`absolute -translate-x-1/2 -translate-y-1/2 text-[10px] font-medium px-1.5 py-0.5 rounded-full border backdrop-blur-sm ${toneCls}`} style={{ left: x, top: y }}>
      {children}
    </div>
  );
}

function Signal({ icon: Icon, label, value, tone, onClick }) {
  const tones = {
    red: "border-rose-500/30 bg-rose-500/10 text-rose-300",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    indigo: "border-indigo-500/30 bg-indigo-500/10 text-indigo-300",
    blue: "border-blue-500/30 bg-blue-500/10 text-blue-300",
    teal: "border-teal-500/30 bg-teal-500/10 text-teal-300",
    slate: "border-slate-600 bg-slate-700/40 text-slate-300",
  };
  return (
    <button onClick={onClick} className={`flex items-center gap-2 rounded-lg border px-3 py-2 hover:brightness-125 transition-all ${tones[tone]}`} data-testid={`sysmap-signal-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="text-lg font-bold font-mono-data leading-none">{value}</span>
      <span className="text-[11px] opacity-80 leading-tight text-left">{label}</span>
    </button>
  );
}

export default function SystemOverview({ bomItems = [], suppliers = [], customers = [], salesOrders = [], orders = [], machines = [], poSummary = {}, onNavigate }) {
  const h = useMemo(() => systemHealth({ bomItems, salesOrders, orders, machines, poSummary }), [bomItems, salesOrders, orders, machines, poSummary]);
  const nodes = systemNodes(h, { suppliers, customers, orders });

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 shadow-sm p-5 text-slate-100" data-testid="system-overview">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-semibold text-white">Visual System Overview</h3>
          <p className="text-xs text-slate-400">Live view of your operation — pulsing red dots flag items that need attention. Click any node to jump in.</p>
        </div>
        <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-emerald-300"><span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> live</span>
      </div>

      <div className="overflow-x-auto pb-2">
        <div className="relative mx-auto rounded-xl overflow-hidden" style={{ width: CANVAS_W, height: CANVAS_H, minWidth: CANVAS_W, background: "radial-gradient(circle at 30% 20%, #1e293b 0%, #0f172a 70%)" }}>
          <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "linear-gradient(#334155 1px, transparent 1px), linear-gradient(90deg, #334155 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
          <style>{`
            @keyframes sysflow { to { stroke-dashoffset: -24; } }
            .sysflow { stroke-dasharray: 6 6; animation: sysflow 1s linear infinite; }
          `}</style>
          <svg className="absolute inset-0" width={CANVAS_W} height={CANVAS_H} viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}>
            <defs>
              {["#3b82f6", "#6366f1", "#10b981", "#f59e0b"].map((c) => (
                <marker key={c} id={`arw-${c.slice(1)}`} markerWidth="9" markerHeight="9" refX="6" refY="3" orient="auto" markerUnits="userSpaceOnUse">
                  <path d="M0,0 L6,3 L0,6 Z" fill={c} />
                </marker>
              ))}
            </defs>
            {SYSTEM_EDGES.map((e) => <path key={e.d} className="sysflow" d={e.d} fill="none" stroke={e.c} strokeWidth="2" markerEnd={`url(#arw-${e.c.slice(1)})`} />)}
          </svg>

          {SYSTEM_EDGE_LABELS.map((l) => <EdgeLabel key={l.text} x={l.x} y={l.y} tone={l.tone}>{l.text}</EdgeLabel>)}

          {nodes.map((n, i) => <Node key={n.id} n={n} index={i} onNavigate={onNavigate} />)}
        </div>
      </div>

      {/* Live health signals + new pieces */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="sysmap-signals">
        <Signal icon={AlertTriangle} label="Low stock" value={h.lowStock} tone={h.lowStock ? "red" : "slate"} onClick={() => onNavigate("bom")} />
        <Signal icon={Cpu} label="Prod. lines" value={h.lines} tone="blue" onClick={() => onNavigate("machines")} />
        <Signal icon={Cog} label="Active jobs" value={h.activeProd} tone="indigo" onClick={() => onNavigate("schedule")} />
        <Signal icon={ShoppingBag} label="Overdue sales" value={h.overdue} tone={h.overdue ? "red" : "slate"} onClick={() => onNavigate("sales")} />
        <Signal icon={History} label="Inventory log" value={h.raw + h.recipes} tone="teal" onClick={() => onNavigate("bom")} />
        <Signal icon={Archive} label="Archived" value={h.archived} tone="slate" onClick={() => onNavigate("bom")} />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] text-slate-400 border-t border-slate-800 pt-3">
        <span className="flex items-center gap-1.5"><span className="h-2 w-4 rounded-full bg-blue-500" /> Procurement</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-4 rounded-full bg-indigo-500" /> Production</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-4 rounded-full bg-emerald-500" /> Sales &amp; fulfilment</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-4 rounded-full bg-amber-500" /> Reorder loop</span>
        <span className="flex items-center gap-1.5 ml-auto"><span className="h-2.5 w-2.5 rounded-full bg-rose-500 animate-pulse" /> needs attention</span>
      </div>
    </div>
  );
}
